import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightSvg } from '~/svg';
import {
  fetchTopReferralSources,
  TopReferralSourcesResponse,
  TopReferrer,
} from '~/services/dashboard';

// Each tab ranks one kind of referrer by campaign requests over the last
// year, and each name links to that referrer's record (HHLTB-2544). Referral
// sources have no detail page, so they open the campaign report filtered to
// that source.
const TABS = [
  {
    key: 'medical_professionals',
    label: 'Med Prof',
    viewAll: '/medical-professionals',
    linkTo: (id: number) => `/medical-professionals/${id}`,
  },
  {
    key: 'medical_centers',
    label: 'Medical Center',
    viewAll: '/medical-centers',
    linkTo: (id: number) => `/medical-centers/${id}`,
  },
  {
    key: 'sources',
    label: 'Source',
    viewAll: null,
    linkTo: (id: number) => `/reports/results?referred_by=${id}`,
  },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const TopReferralSources = () => {
  const [data, setData] = useState<TopReferralSourcesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('medical_professionals');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchTopReferralSources();
        if (cancelled) return;
        setData(resp);
        // Open on the first tab that has anyone in it
        const firstWithData = TABS.find((tab) => resp[tab.key]?.length);
        if (firstWithData) setActiveTab(firstWithData.key);
      } catch (error) {
        console.error('Error fetching top referral sources:', error);
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tab = TABS.find((t) => t.key === activeTab) ?? TABS[0];
  const rows: TopReferrer[] = data?.[tab.key] ?? [];

  return (
    <div className='h-full rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark md:p-6 xl:p-7.5'>
      <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h2 className='text-xl font-semibold text-black dark:text-white'>
            Top Referral Sources
          </h2>
          <p className='text-sm text-secondary'>Last 12 months</p>
        </div>

        {tab.viewAll && (
          <Link
            to={tab.viewAll}
            className='flex items-center gap-2 text-primary'
          >
            View All <ArrowRightSvg />
          </Link>
        )}
      </div>

      <div className='mb-2 flex flex-wrap gap-2' role='tablist'>
        {TABS.map((t) => (
          <button
            key={t.key}
            type='button'
            role='tab'
            aria-selected={t.key === activeTab}
            onClick={() => setActiveTab(t.key)}
            className={`rounded-md px-3 py-1 text-sm ${
              t.key === activeTab
                ? 'bg-primary text-white'
                : 'bg-gray text-black hover:bg-opacity-80 dark:bg-meta-4 dark:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className='flex items-center justify-center py-8'>
          <p className='text-secondary'>Loading...</p>
        </div>
      ) : failed ? (
        <div className='flex items-center justify-center py-8'>
          <p className='text-secondary'>Could not load referral sources</p>
        </div>
      ) : rows.length === 0 ? (
        <div className='flex items-center justify-center py-8'>
          <p className='text-secondary'>No referrals in the last 12 months</p>
        </div>
      ) : (
        <div className='flex flex-col gap-2'>
          <div className='grid grid-cols-12 py-2'>
            <div className='col-span-9'>
              <p className='text-sm text-secondary'>{tab.label}</p>
            </div>
            <div className='col-span-3'>
              <p className='text-right text-sm text-secondary'>Referrals</p>
            </div>
          </div>
          {rows.map((row) => (
            <div key={row.id} className='relative z-1 grid grid-cols-12 py-2'>
              <div className='col-span-9'>
                <Link to={tab.linkTo(row.id)} className='text-primary hover:underline'>
                  {row.name}
                </Link>
              </div>
              <div className='col-span-3'>
                <p className='text-right'>{row.referrals.toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TopReferralSources;
